/* eslint-disable */
import { useState } from "react";
import { MessageCircle, CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { propertiesApi } from "./api";
import TurnstileWidget from "../captcha/TurnstileWidget";
import "./ExternalContactForm.css";

const STEPS = { FORM: "form", OTP: "otp", DONE: "done" };

export default function ExternalContactForm({ listing }) {
  const [step, setStep] = useState(STEPS.FORM);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const cleanPhone = (raw) =>
    String(raw || "").replace(/\D/g, "").slice(-10);

  const onSendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Please share your name.");
      return;
    }
    const digits = cleanPhone(phone);
    if (digits.length !== 10) {
      setError("Phone must be a 10-digit Indian mobile number.");
      return;
    }
    if (!captchaToken) {
      setError("Please complete the captcha challenge.");
      return;
    }
    setBusy(true);
    try {
      await propertiesApi.externalOtpStart(listing.id, {
        name: name.trim(),
        phone: digits,
        captcha_token: captchaToken,
      });
      setMaskedPhone(`+91 ${digits.slice(0, 5)} ${digits.slice(5)}`);
      setStep(STEPS.OTP);
    } catch (err) {
      setError(err.message || "Could not send code");
      // Turnstile tokens are one-shot; force a fresh challenge so the
      // user can retry without reloading the page.
      setCaptchaToken("");
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (code.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      await propertiesApi.externalOtpVerify(listing.id, {
        name: name.trim(),
        phone: cleanPhone(phone),
        code,
      });
      setStep(STEPS.DONE);
    } catch (err) {
      setError(err.message || "Could not verify");
    } finally {
      setBusy(false);
    }
  };

  if (step === STEPS.DONE) {
    return (
      <div className="vsp-x-card vsp-x-success">
        <CheckCircle2 size={28} color="var(--accent)" strokeWidth={2.1} />
        <h3>Owner details sent</h3>
        <p>
          We've messaged <strong>{listing.owner?.name}</strong>'s contact
          details to your WhatsApp at <strong>{maskedPhone}</strong>.
        </p>
        <p className="vsp-x-foot">
          {listing.owner?.name?.split(" ")[0] || "The owner"} has also been
          notified that you requested their contact.
        </p>
      </div>
    );
  }

  if (step === STEPS.OTP) {
    return (
      <form className="vsp-x-card" onSubmit={onVerifyOtp}>
        <div className="vsp-x-head">
          <Lock size={18} strokeWidth={2.1} />
          <h3>Enter the code</h3>
        </div>
        <p className="vsp-x-help">
          We sent a 6-digit code to your WhatsApp at{" "}
          <strong>{maskedPhone}</strong>.
        </p>
        <div className="vsp-x-field">
          <label htmlFor="otp">6-digit code</label>
          <input
            id="otp"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            autoFocus
          />
        </div>
        {error && <div className="vsp-x-error">{error}</div>}
        <div className="vsp-x-actions">
          <button
            type="button"
            className="vsp-x-btn-secondary"
            onClick={() => {
              setStep(STEPS.FORM);
              setCode("");
              setError("");
            }}
            disabled={busy}
          >
            Change number
          </button>
          <button
            type="submit"
            className="vsp-x-btn-primary"
            disabled={busy || code.length !== 6}
          >
            {busy ? "Verifying…" : "Verify & get contact"}
            <ArrowRight size={13} />
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="vsp-x-card" onSubmit={onSendOtp}>
      <div className="vsp-x-head">
        <MessageCircle size={18} strokeWidth={2.1} />
        <h3>Get owner contact via WhatsApp</h3>
      </div>
      <p className="vsp-x-help">
        We'll send the owner's number directly to your WhatsApp once you
        verify your phone. The owner will also be notified of your interest.
      </p>
      <div className="vsp-x-field">
        <label htmlFor="ext-name">Your name</label>
        <input
          id="ext-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={120}
          autoComplete="name"
        />
      </div>
      <div className="vsp-x-field">
        <label htmlFor="ext-phone">Mobile number</label>
        <div className="vsp-x-phone">
          <span>+91</span>
          <input
            id="ext-phone"
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) =>
              setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            placeholder="9876543210"
            autoComplete="tel-national"
            maxLength={10}
          />
        </div>
      </div>
      <div className="vsp-x-captcha">
        <TurnstileWidget
          onToken={setCaptchaToken}
          onExpire={() => setCaptchaToken("")}
          onError={() => setCaptchaToken("")}
          resetKey={captchaResetKey}
        />
      </div>
      {error && <div className="vsp-x-error">{error}</div>}
      <button
        type="submit"
        className="vsp-x-btn-primary vsp-x-btn-block"
        disabled={busy || !captchaToken}
      >
        {busy ? "Sending OTP…" : "Send me an OTP on WhatsApp"}
        <ArrowRight size={13} />
      </button>
      <p className="vsp-x-foot">
        By continuing you agree that the villa owner will see your name and
        mobile number.
      </p>
    </form>
  );
}
